import { useState } from "react"
import moment from "moment"
import { apiErrorMessage, createTricount, destroyTricount, previewTricountFiling, updateTricount } from "../../ApiInterface"
import { useAccounts } from "../../AccountsContext"
import Modal from "../../components/Modal"
import { useFilingConfirmation } from "./FilingModal"

const NEW_ACCOUNT = ''

export interface TricountNotice {
    message: string
    error: string
}

interface Props {
    tricounts: Tricount[]
    reloadTricounts: () => Promise<void>
    // Messages de la carte, y compris ceux de « Tout synchroniser »
    notice: TricountNotice
    setNotice: (notice: TricountNotice) => void
}

const formatSync = (date: string | null) => date ? moment(date).format('DD/MM/YYYY HH:mm') : 'jamais'

// Tricount rejoints par leur lien de partage, dans une seule carte comme une connexion bancaire.
// Comme un compte bancaire, un Tricount activé alimente un compte de la navigation : un nouveau
// compte, ou un compte saisi à la main où on le recopiait. Ses dépenses y sont importées au montant
// total, avec ma part en impact. La synchro se lance depuis « Tout synchroniser », en haut de page.
const TricountsSettings = ({ tricounts, reloadTricounts, notice, setNotice }: Props) => {
    const [link, setLink] = useState('')
    const [adding, setAdding] = useState(false)
    // Compte choisi avant l'activation, par Tricount
    const [chosenAccountIds, setChosenAccountIds] = useState({} as Record<number, string>)
    const [tricountToRemove, setTricountToRemove] = useState(null as Tricount | null)
    const [removing, setRemoving] = useState(false)
    const { accounts, reloadAccounts } = useAccounts()
    const { confirm, modal: filingModal } = useFilingConfirmation()

    const fail = (fallback: string) => (e: any) => setNotice({ message: '', error: apiErrorMessage(e, fallback) })

    const add = (e: React.FormEvent) => {
        e.preventDefault()
        if(link.trim() === '') {
            return
        }
        setAdding(true)
        setNotice({ message: '', error: '' })
        createTricount(link.trim())
            .then(() => {
                setLink('')
                setNotice({ message: 'Tricount ajouté : indiquez qui vous êtes, puis activez l\'import', error: '' })
                return reloadTricounts()
            })
            .catch(fail('Impossible d\'ajouter ce Tricount'))
            .finally(() => setAdding(false))
    }

    const save = (tricount: Tricount, params: TricountParams) => {
        setNotice({ message: params.enabled ? 'Synchronisation...' : '', error: '' })
        return updateTricount(tricount.id, params)
            .then(() => {
                if(params.enabled) {
                    setNotice({ message: `Tricount « ${tricount.title} » importé`, error: '' })
                }
                reloadTricounts()
                reloadAccounts()
            })
            .catch(fail('L\'enregistrement a échoué'))
    }

    const enable = (tricount: Tricount, enabled: boolean) => {
        const accountId = chosenAccountIds[tricount.id]
        const params: TricountParams = { enabled }
        if(enabled && !tricount.account_id && accountId && accountId !== NEW_ACCOUNT) {
            params.account_id = Number(accountId)
        }
        save(tricount, params)
    }

    // Changer de rangement re-range l'historique du compte : aperçu, puis confirmation.
    const saveFiling = (tricount: Tricount, filename_mode: TricountFilenameMode) => {
        const params = { filename_mode }
        previewTricountFiling(tricount.id, params)
            .then((preview) => confirm(
                `Ranger « ${tricount.title || 'Tricount'} » ${filename_mode === 'monthly' ? 'par mois' : 'dans un seul relevé'}`,
                preview,
                () => save(tricount, params)
            ))
            .catch(fail('L\'enregistrement a échoué'))
    }

    const remove = (tricount: Tricount) => {
        setRemoving(true)
        destroyTricount(tricount.id)
            .then(() => {
                setTricountToRemove(null)
                reloadTricounts()
                reloadAccounts()
            })
            .catch(fail('Impossible de retirer ce Tricount'))
            .finally(() => setRemoving(false))
    }

    // Comptes saisis à la main, que la synchro peut reprendre.
    const manualAccounts = accounts.filter((account) => !account.synced)
    const accountName = (id: number | null) => accounts.find((account) => account.id === id)?.name

    const enabledCount = tricounts.filter((tricount) => tricount.enabled).length
    const toSetUpCount = tricounts.length - enabledCount
    const lastSyncedAt = tricounts
        .map((tricount) => tricount.last_synced_at)
        .filter((date): date is string => !!date)
        .sort()
        .pop() || null

    return <div className="tl-card bank-connection tricounts">
        { filingModal }
        { tricountToRemove && <Modal onCancel={() => setTricountToRemove(null)}>
            <div className="remove-account-modal">
                <h3>Retirer « { tricountToRemove.title || 'Tricount' } » ?</h3>
                <p>Ses dépenses ne seront plus synchronisées.</p>
                { tricountToRemove.account_id && <p className="muted">
                    Le compte « { accountName(tricountToRemove.account_id) } » et ses opérations sont gardés, comme un compte saisi à la main.
                </p> }
                <div className="buttons">
                    <button type="button" className="tl-btn tl-btn-ghost" onClick={() => setTricountToRemove(null)}>Annuler</button>
                    <button type="button" className="tl-btn tl-btn-primary" disabled={removing} onClick={() => remove(tricountToRemove)} autoFocus>
                        { removing ? 'Retrait…' : 'Retirer le Tricount' }
                    </button>
                </div>
            </div>
        </Modal> }

        <div className="bank-connection-head">
            <div>
                <h3>Tricount</h3>
                <div className="status">
                    { enabledCount > 0 && <span className="tl-badge tl-badge-ok">{ enabledCount } importé{ enabledCount > 1 ? 's' : '' }</span> }
                    { toSetUpCount > 0 && <span className="tl-badge tl-badge-todo">{ toSetUpCount } à configurer</span> }
                    { enabledCount > 0
                        ? <span className="last-sync">Dernière synchronisation : { formatSync(lastSyncedAt) }</span>
                        : <span className="last-sync">Dépenses partagées importées avec votre part en impact</span> }
                </div>
            </div>
        </div>

        { notice.error && <div className="tl-alert tl-alert-danger">{ notice.error }</div> }
        { notice.message && <div className="tl-alert">{ notice.message }</div> }

        { tricounts.length > 0 && <table className="bank-accounts">
            <thead>
                <tr>
                    <th>Importer</th>
                    <th>Tricount</th>
                    <th>Je suis</th>
                    <th>Compte</th>
                    <th>Relevés</th>
                    <th/>
                </tr>
            </thead>
            <tbody>
                { tricounts.map((tricount) => <tr key={tricount.id} className={tricount.enabled ? '' : 'disabled'}>
                    <td className="enabled">
                        <label className="switch" title={tricount.membership_uuid ? '' : 'Indiquez d\'abord qui vous êtes'}>
                            <input type="checkbox" checked={tricount.enabled} disabled={!tricount.membership_uuid}
                                onChange={(e) => enable(tricount, e.target.checked)}/>
                            <span/>
                        </label>
                    </td>
                    <td className="name">
                        { tricount.title || 'Tricount' }
                        <small>{ tricount.enabled ? `Synchronisé : ${formatSync(tricount.last_synced_at)}` : 'Pas encore importé' }</small>
                    </td>
                    <td>
                        <select className={`tl-select ${tricount.membership_uuid ? '' : 'to-set-up'}`} value={tricount.membership_uuid || ''}
                            onChange={(e) => save(tricount, { membership_uuid: e.target.value })}>
                            { !tricount.membership_uuid && <option value="">Choisir…</option> }
                            { tricount.members.map((member) => <option key={member.uuid} value={member.uuid}>{ member.name }</option>) }
                        </select>
                    </td>
                    <td className="name">
                        { tricount.account_id
                            ? accountName(tricount.account_id)
                            : <select className="tl-select" value={chosenAccountIds[tricount.id] || NEW_ACCOUNT}
                                onChange={(e) => setChosenAccountIds({ ...chosenAccountIds, [tricount.id]: e.target.value })}>
                                <option value={NEW_ACCOUNT}>Nouveau compte « { tricount.title } »</option>
                                { manualAccounts.map((account) => <option key={account.id} value={account.id}>
                                    Reprendre « { account.name } »
                                </option>) }
                            </select> }
                    </td>
                    <td>
                        <select className="tl-select" value={tricount.filename_mode}
                            onChange={(e) => saveFiling(tricount, e.target.value as TricountFilenameMode)}>
                            <option value="monthly">Par mois</option>
                            <option value="fixed">Un seul relevé</option>
                        </select>
                    </td>
                    <td className="row-actions">
                        <a className="icon-button" href={tricount.public_url} target="_blank" rel="noreferrer" title="Ouvrir dans Tricount">
                            <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M14 4h6v6M20 4l-9 9M18 14v5a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h5"/></svg>
                        </a>
                        <button className="icon-button" onClick={() => setTricountToRemove(tricount)} title="Retirer">
                            <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 6l12 12M18 6 6 18"/></svg>
                        </button>
                    </td>
                </tr>) }
            </tbody>
        </table> }

        <form className="new-tricount" onSubmit={add}>
            <input className="tl-input" aria-label="Lien de partage Tricount" placeholder="Coller un lien de partage : https://tricount.com/XXXXXXXX"
                value={link} onChange={(e) => setLink(e.target.value)}/>
            <button type="submit" className="tl-btn tl-btn-secondary" disabled={adding || link.trim() === ''}>
                { adding ? 'Ajout...' : 'Ajouter' }
            </button>
        </form>
    </div>
}

export default TricountsSettings
